// "Continue with Google": the portal half of api/googleAuth.
//
// The round trip is a top-level navigation (portal → API → Google → API →
// /auth/google), so the page the person started from and its query string
// (funnel / checkout parameters, ?redirect) are kept here, in
// sessionStorage, and restored on return. Navigation hints only — never
// credentials: the verified identity lives in the API's session cookie.

import config from "../../config";
import axiosInstant from "../../api";
import { postForgotPassword } from "../../api/endpoints";

/** "connect" = Settings › Sign-in methods › Connect (never signs anyone in). */
export type GoogleIntent = "brand" | "expert" | "login" | "connect";

export interface GoogleReturnContext {
    intent: GoogleIntent;
    /** Path + query to go back to (the form, or the login page for ?redirect). */
    returnPath: string;
    /** Set on the RMN-branded login so the result stays in that portal. */
    rmnSlug?: string;
}

export interface GoogleSession {
    flowId: string;
    intent: GoogleIntent;
    email: string;
    firstName: string;
    lastName: string;
    next: "login" | "link" | "register" | "no_account" | "refused" | "connect";
    code?: string;
    hasPassword?: boolean;
    brandAllowed?: boolean;
}

export interface GoogleSignedIn {
    status: "signed_in";
    id: number;
    tokens: { token: string; refreshToken: string };
    first_product_queued?: boolean;
}

/** One row of Settings › Sign-in methods (api/signInMethods). */
export type SignInMethodRow =
    | { method: "password"; email: string; set: boolean }
    | { method: "google"; email: string | null; status: "connected" | "needs_reconnecting" | "not_connected" };

export interface SignInMethods {
    methods: SignInMethodRow[];
}

const CONTEXT_KEY = "tdc.googleSignIn";
const GOOGLE_PATH = "/auth/google";

export function saveGoogleContext(ctx: GoogleReturnContext) {
    try {
        sessionStorage.setItem(CONTEXT_KEY, JSON.stringify(ctx));
    } catch {
        // Private mode can refuse storage; the return page then falls back to "/".
    }
}

export function takeGoogleContext(): GoogleReturnContext | null {
    try {
        const raw = sessionStorage.getItem(CONTEXT_KEY);
        sessionStorage.removeItem(CONTEXT_KEY);
        return raw ? (JSON.parse(raw) as GoogleReturnContext) : null;
    } catch {
        return null;
    }
}

// Control characters are stripped by URL parsers, which would let
// "/<tab>/evil.example" turn into a protocol-relative URL.
const hasControlOrBackslash = (s: string) =>
    s.split("").some((ch) => ch === "\\" || ch.charCodeAt(0) < 32 || ch.charCodeAt(0) === 127);

/** A same-origin path only: one leading slash, no scheme, no `//`. */
export function safeReturnPath(path: string | null | undefined): string {
    if (!path || hasControlOrBackslash(path)) return "/";
    try {
        const url = new URL(path, window.location.origin);
        if (url.origin !== window.location.origin) return "/";
        return `${url.pathname}${url.search}`;
    } catch {
        return "/";
    }
}

/** Leaves the page for Google. `loginHint` preselects a Google account. */
export function startGoogleSignIn(ctx: GoogleReturnContext, loginHint?: string) {
    saveGoogleContext({ ...ctx, returnPath: safeReturnPath(ctx.returnPath) });
    const params = new URLSearchParams({ intent: ctx.intent });
    if (loginHint) params.set("login_hint", loginHint);
    window.location.assign(`${config.API_URL}${GOOGLE_PATH}/start?${params.toString()}`);
}

// The Google routes read the API's session cookie, so these requests carry
// credentials. Nothing else in the portal does: the API's CORS policy
// allows credentials for these routes only.
const withCreds = { withCredentials: true as const };

export const fetchGoogleSession = () =>
    axiosInstant.get<GoogleSession>(`${GOOGLE_PATH}/session`, withCreds).then((r) => r.data);

export const googleLogin = (flowId: string) =>
    axiosInstant.post<GoogleSignedIn>(`${GOOGLE_PATH}/login`, { flowId }, withCreds).then((r) => r.data);

export const googleLink = (flowId: string, password: string) =>
    axiosInstant.post<GoogleSignedIn>(`${GOOGLE_PATH}/link`, { flowId, password }, withCreds).then((r) => r.data);

export const googleRegisterBrand = (flowId: string, body: Record<string, unknown>) =>
    axiosInstant
        .post<GoogleSignedIn>(`${GOOGLE_PATH}/register/brand`, { ...body, flowId }, withCreds)
        .then((r) => r.data);

export const googleRegisterExpert = (flowId: string, body: Record<string, unknown>) =>
    axiosInstant
        .post<GoogleSignedIn>(`${GOOGLE_PATH}/register/expert`, { ...body, flowId }, withCreds)
        .then((r) => r.data);

// Settings › Sign-in methods. Connect carries the Google flow cookie and the
// signed-in user's token (added by the axios interceptor); the API attaches
// Google to that user only. The list and disconnect use the token alone.
export const googleConnect = (flowId: string, password: string) =>
    axiosInstant
        .post<SignInMethods & { status: "connected" }>(`${GOOGLE_PATH}/connect`, { flowId, password }, withCreds)
        .then((r) => r.data);

export const fetchSignInMethods = () =>
    axiosInstant.get<SignInMethods>("users/me/sign-in-methods").then((r) => r.data);

export const disconnectGoogle = (password: string) =>
    axiosInstant
        .post<SignInMethods & { status: "disconnected" }>("users/me/sign-in-methods/google/disconnect", { password })
        .then((r) => r.data);

/** "Set a password": the existing Forgot password email, sent to the account email. */
export const sendPasswordSetupEmail = (emailAddress: string) =>
    axiosInstant.post(postForgotPassword(), { emailAddress });

/** The code the API sent, whether as a bare string or `{ error }`. */
export function googleErrorCode(err: unknown): string {
    const data = (err as { response?: { data?: unknown } })?.response?.data;
    if (typeof data === "string") return data;
    const code = (data as { error?: unknown } | undefined)?.error;
    return typeof code === "string" ? code : "GoogleSignInFailed";
}

// Fixed messages: the code can come from the URL, so an unknown one never
// renders as-is.
const MESSAGES: Record<string, string> = {
    unavailable: "Google sign-in isn't available right now. Use your email and password.",
    busy: "Too many Google sign-in attempts from your network. Wait a little and try again, or use your email and password.",
    GoogleSignInUnavailable: "Google sign-in isn't available right now. Use your email and password.",
    cancelled: "Google sign-in was cancelled.",
    expired: "Your Google sign-in expired or was replaced. Please start again.",
    GoogleSessionExpired: "Your Google sign-in expired or was replaced. Please start again.",
    FlowChanged: "Your Google sign-in changed in another tab. Please start again.",
    GoogleEmailNotVerified: "Google hasn't verified this account's email, so it can't be used here.",
    GoogleEmailChanged:
        "This Google account is connected to an account whose email has changed. Log in with your email and password (or use Forgot password if you don't have one), then connect a Google account that uses your current email in Settings › Sign-in Methods.",
    GoogleEmailMismatch: "That Google account uses a different email. Choose a Google account that uses your account's email.",
    GoogleNotAvailableForAccount: "Google sign-in isn't available for this account. Use your email and password.",
    EmailDomainNotAllowed: "Brand accounts need a business email. Personal Google accounts like @gmail.com can't register a brand.",
    DuplicateAccounts: "We found more than one account for this email. Contact help@thedesirecompany.com.",
    NoPasswordSet: "This account has no password yet. Use Forgot password to set one, then continue with Google.",
    LoginCredentialsIncorrect: "That password isn't right.",
    LinkLocked: "Too many wrong passwords. Try again in an hour.",
    GoogleAccountAlreadyLinked:
        "This Google account is connected to another account. Disconnect it there first, or choose a different Google account.",
    PersonalKeyNotAllowed: "Sign-in methods can't be changed with an API key.",
    GoogleLinkFailed: "Your account was created but couldn't be connected to Google. Use Forgot password to set a password.",
    rate_limit_exceeded: "Too many attempts. Please try again later.",
    // An account already uses this Google email (a retry after a half-done
    // sign-up, or one made in another tab): log in, don't sign up again.
    AccountExists: "An account already uses this Google email. Log in instead, with Google or your password (Forgot password sets one).",
    NotRegistrable: "An account already uses this Google email. Log in instead, with Google or your password (Forgot password sets one).",
    NotLinked: "This Google account isn't connected to an account yet. Please start again.",
    NotLinkable: "This Google account can't be connected here. Please start again.",
    GoogleNotConnected: "Google isn't connected to this account.",
    BadOrigin: "Google sign-in didn't work. Please try again.",
    GoogleSignInFailed: "Google sign-in didn't work. Please try again.",
    failed: "Google sign-in didn't work. Please try again."
};

// Own keys only: a URL value such as "__proto__" or "constructor" must fall
// back to the fixed message, not reach an Object.prototype member.
export const googleMessage = (code: string | null | undefined) =>
    code && Object.prototype.hasOwnProperty.call(MESSAGES, code) ? MESSAGES[code] : MESSAGES.GoogleSignInFailed;
